<template>
    <!--
        ===================================================================
        AI Pre-Population — Step 3 of Add/Edit Vendor wizard
        ===================================================================
        Backs project_ai_prepopulation_and_vendor_confirmation.
        Stage 1 (Gather Sources) is wired live against the AnswerBank.Service
        vendor-source endpoints. Stages 2 (Coverage Estimate) and 3 (Run
        AI Pre-Population) are placeholders — they ship in the next slice
        once the orchestrator endpoints exist.
        -->
    <div class="ai-prepop-step">
        <!-- Optional pill + short helper. The step's full title is
             rendered by the parent (AddPartner.vue's step-title h4). -->
        <div class="ai-prepop-subtitle">
            <span class="ai-prepop-optional-pill">Optional</span>
            <span class="ai-prepop-subtitle-hint">
                You can skip this step — the vendor can also add source material later.
            </span>
        </div>

        <!-- Intro call-out -->
        <div class="ai-prepop-intro">
            <span class="ai-prepop-intro-icon">✦</span>
            <span>
                Let AI give this vendor a head start. Provide the vendor's website URLs and upload up to 5
                supporting documents. Fetch each URL to confirm it is accessible and contains useful content.
                Once your sources are confirmed, run AI pre-population to map the extracted content against
                the vendor's Answer Bank. The vendor will be prompted to review, confirm, and complete any
                remaining fields before their profile goes live.
            </span>
        </div>

        <!-- ============================================================ -->
        <!-- STAGE 1 — Gather Sources                                     -->
        <!-- ============================================================ -->
        <div class="ai-prepop-stage">
            <div class="ai-prepop-stage-header">
                <div class="ai-prepop-stage-num">1</div>
                <div class="ai-prepop-stage-title">Gather sources</div>
            </div>

            <!-- URLs -->
            <div class="ai-prepop-substage">
                <div class="ai-prepop-label-row">
                    <span class="ai-prepop-label">Website URLs</span>
                    <span class="ai-prepop-label-hint">optional — up to {{ MAX_URLS }} URLs</span>
                </div>

                <div
                    v-for="(row, idx) in urlRows"
                    :key="row.id || `draft-${idx}`"
                    class="ai-prepop-url-row"
                >
                    <InputText
                        v-model="row.url"
                        :disabled="!!row.id || row.fetching"
                        placeholder="https://vendor-website.com/services"
                        class="ai-prepop-url-input"
                    />
                    <template v-if="!row.id">
                        <Button
                            :label="row.fetching ? 'Fetching…' : 'Fetch'"
                            :disabled="!row.url || row.fetching"
                            @click="fetchUrl(idx)"
                            class="p-button-outlined ai-prepop-fetch-btn"
                        />
                        <!-- Draft rows can be removed too — but never
                             let the last remaining row disappear; there
                             always needs to be at least one input for
                             the vendor to type into. -->
                        <Button
                            v-if="urlRows.length > 1"
                            icon="pi pi-times"
                            class="p-button-text p-button-rounded p-button-plain ai-prepop-icon-btn"
                            @click="removeDraftUrl(idx)"
                            :disabled="row.fetching"
                            aria-label="Remove URL slot"
                        />
                    </template>
                    <Button
                        v-else
                        icon="pi pi-times"
                        class="p-button-text p-button-rounded p-button-plain ai-prepop-icon-btn"
                        @click="removeUrl(idx)"
                        :disabled="row.deleting"
                        aria-label="Remove URL"
                    />
                </div>

                <!-- Status pill -->
                <div
                    v-for="(row, idx) in urlRows"
                    :key="`status-${row.id || idx}`"
                    v-show="row.id && row.fetch_status"
                    class="ai-prepop-status-pill"
                    :class="`status-${row.fetch_status}`"
                >
                    <span class="ai-prepop-status-icon">
                        <template v-if="row.fetch_status === 'retrieved'">✅</template>
                        <template v-else-if="row.fetch_status === 'partial'">⚠️</template>
                        <template v-else-if="row.fetch_status === 'failed'">❌</template>
                    </span>
                    <span class="ai-prepop-status-text">
                        <strong>{{ statusLabel(row.fetch_status) }}</strong>
                        — {{ urlStatusMessage(row) }}
                    </span>
                </div>

                <Button
                    v-if="urlRows.length < MAX_URLS"
                    label="Add another URL"
                    icon="pi pi-plus"
                    @click="addUrlRow"
                    class="p-button-outlined ai-prepop-add-url"
                />
            </div>

            <!-- Documents -->
            <div class="ai-prepop-substage">
                <div class="ai-prepop-label-row">
                    <span class="ai-prepop-label">Supporting documents</span>
                    <span class="ai-prepop-label-hint">optional — up to {{ MAX_DOCS }} files</span>
                </div>

                <div
                    class="ai-prepop-dropzone"
                    :class="{ 'dragover': isDragOver, 'disabled': documents.length >= MAX_DOCS }"
                    @dragover.prevent="onDragOver"
                    @dragleave.prevent="onDragLeave"
                    @drop.prevent="onDrop"
                >
                    <div class="ai-prepop-dropzone-text">
                        Drag and drop files here — PDF, DOCX, or PPTX · Max 20 MB per file
                    </div>
                    <Button
                        label="Browse files"
                        icon="pi pi-folder-open"
                        class="p-button-outlined ai-prepop-browse-btn"
                        :disabled="documents.length >= MAX_DOCS"
                        @click="openFilePicker"
                    />
                    <input
                        ref="fileInput"
                        type="file"
                        accept=".pdf,.docx,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                        multiple
                        style="display: none;"
                        @change="onFilesChosen"
                    />
                </div>

                <div class="ai-prepop-doc-counter">
                    {{ documents.length }} of {{ MAX_DOCS }} documents uploaded
                </div>

                <!-- Doc rows -->
                <div v-for="doc in documents" :key="doc.id || doc.tempKey" class="ai-prepop-doc-row">
                    <div class="ai-prepop-doc-info">
                        <span class="ai-prepop-doc-name">{{ doc.file_name }}</span>
                        <span class="ai-prepop-doc-size">{{ formatBytes(doc.file_size_bytes) }}</span>
                    </div>
                    <div class="ai-prepop-doc-status">
                        <span v-if="doc.uploading" class="ai-prepop-doc-progress">
                            Uploading {{ doc.progress }}%
                        </span>
                        <span v-else-if="doc.upload_status === 'processed'" class="ai-prepop-doc-words">
                            ✅ {{ doc.word_count ? doc.word_count.toLocaleString() + ' words' : 'parsed' }}
                        </span>
                        <span v-else-if="doc.upload_status === 'failed'" class="ai-prepop-doc-failed">
                            ❌ {{ doc.error_message || 'Failed' }}
                        </span>
                    </div>
                    <Button
                        v-if="!doc.uploading"
                        icon="pi pi-times"
                        class="p-button-text p-button-rounded p-button-plain ai-prepop-icon-btn"
                        @click="removeDocument(doc)"
                        :disabled="doc.deleting"
                        aria-label="Remove document"
                    />
                </div>
            </div>
        </div>

        <!-- ============================================================ -->
        <!-- STAGE 2 — Review source summary                              -->
        <!-- ============================================================ -->
        <div class="ai-prepop-stage" :class="{ disabled: !hasUsableSources }">
            <div class="ai-prepop-stage-header">
                <div class="ai-prepop-stage-num">2</div>
                <div class="ai-prepop-stage-title">Review source summary</div>
                <Button
                    v-if="hasUsableSources && !coverageLoading"
                    label="Refresh estimate"
                    class="p-button-text p-button-sm ai-prepop-refresh-btn"
                    icon="pi pi-refresh"
                    @click="fetchCoverage"
                />
            </div>

            <div v-if="!hasUsableSources" class="ai-prepop-stage-placeholder">
                Add at least one Retrieved URL or processed document above to see the coverage estimate.
            </div>

            <div v-else-if="coverageLoading" class="ai-prepop-stage-placeholder">
                <i class="pi pi-spin pi-spinner"></i>
                Estimating coverage — this takes a few seconds…
            </div>

            <div v-else-if="coverage" class="ai-prepop-coverage-block">
                <div class="ai-prepop-stat-cards">
                    <div class="ai-prepop-stat-card">
                        <div class="ai-prepop-stat-value">{{ coverage.urls_fetched }}</div>
                        <div class="ai-prepop-stat-label">URLs successfully fetched</div>
                    </div>
                    <div class="ai-prepop-stat-card">
                        <div class="ai-prepop-stat-value">{{ coverage.documents_processed }}</div>
                        <div class="ai-prepop-stat-label">Documents processed</div>
                    </div>
                    <div class="ai-prepop-stat-card">
                        <div class="ai-prepop-stat-value">{{ formatNumber(coverage.total_words) }}</div>
                        <div class="ai-prepop-stat-label">Words available for extraction</div>
                    </div>
                    <div class="ai-prepop-stat-card ai-prepop-stat-card-highlight">
                        <div class="ai-prepop-stat-value">~{{ coverage.estimated_coverage_pct }}%</div>
                        <div class="ai-prepop-stat-label">Estimated Answer Bank coverage</div>
                    </div>
                </div>

                <details v-if="coverage.by_section && coverage.by_section.length" class="ai-prepop-section-breakdown">
                    <summary>Per-section coverage breakdown</summary>
                    <div class="ai-prepop-section-list">
                        <div
                            v-for="s in coverage.by_section"
                            :key="s.category_id"
                            class="ai-prepop-section-row"
                        >
                            <span class="ai-prepop-section-name">{{ s.category_name }}</span>
                            <span class="ai-prepop-section-count">
                                {{ s.likely_fillable }} / {{ s.total }} likely
                            </span>
                            <div class="ai-prepop-section-bar">
                                <div
                                    class="ai-prepop-section-bar-fill"
                                    :style="{ width: (s.coverage_pct || 0) + '%' }"
                                ></div>
                            </div>
                            <span class="ai-prepop-section-pct">{{ s.coverage_pct }}%</span>
                        </div>
                    </div>
                </details>
            </div>

            <div v-else-if="coverageError" class="ai-prepop-stage-error">
                Could not estimate coverage — {{ coverageError }}.
                <Button
                    label="Try again"
                    class="p-button-text p-button-sm"
                    @click="fetchCoverage"
                />
            </div>
        </div>

        <!-- ============================================================ -->
        <!-- STAGE 3 — Run AI pre-population                              -->
        <!-- ============================================================ -->
        <div class="ai-prepop-stage" :class="{ disabled: !hasUsableSources }">
            <div class="ai-prepop-stage-header">
                <div class="ai-prepop-stage-num">3</div>
                <div class="ai-prepop-stage-title">Run AI pre-population</div>
            </div>

            <div v-if="!hasUsableSources" class="ai-prepop-stage-placeholder">
                Confirm at least one source in Stage 1 to enable this step.
            </div>

            <template v-else>
                <div class="ai-prepop-scope-label">Pre-population scope</div>
                <div class="ai-prepop-scope-options">
                    <label
                        class="ai-prepop-scope-card"
                        :class="{ selected: scope === 'fill_empty_only' }"
                    >
                        <input
                            type="radio"
                            value="fill_empty_only"
                            v-model="scope"
                            :disabled="runInProgress"
                        />
                        <div>
                            <div class="ai-prepop-scope-title">Fill empty answers only</div>
                            <div class="ai-prepop-scope-desc">
                                Only writes to questions with no current answer.
                                Never touches what the vendor has already provided or confirmed.
                            </div>
                        </div>
                    </label>
                    <label
                        class="ai-prepop-scope-card"
                        :class="{ selected: scope === 'overwrite_all' }"
                    >
                        <input
                            type="radio"
                            value="overwrite_all"
                            v-model="scope"
                            :disabled="runInProgress"
                        />
                        <div>
                            <div class="ai-prepop-scope-title">Overwrite and refresh all answers</div>
                            <div class="ai-prepop-scope-desc">
                                Re-evaluates every question — replaces existing answers and
                                resets section confirmation on anything that changed.
                                <strong>If the vendor is currently Live, their profile will be
                                returned to Draft</strong> so they can re-review before it goes
                                back to clients.
                            </div>
                        </div>
                    </label>
                </div>

                <!-- Confirmation summary + Run — shown when there's no
                     last-run in memory (fresh vendor, or lastRun was
                     cleared because sources changed). -->
                <div class="ai-prepop-run-summary" v-if="!lastRun">
                    <div>
                        <strong>Ready to pre-populate</strong>
                        — {{ coverage ? coverage.urls_fetched : 0 }} URLs and
                        {{ coverage ? coverage.documents_processed : 0 }} documents confirmed ·
                        {{ scopeLabel }} ·
                        {{ vendorName || "vendor's" }} Answer Bank will be updated and vendor
                        notified to review.
                        <template v-if="scope === 'overwrite_all'">
                            If the vendor is currently Live, this run will return their profile
                            to Draft so they can re-review before it goes back to clients.
                        </template>
                    </div>
                    <Button
                        :label="runInProgress ? 'Running…' : '✦ Run AI Pre-Population'"
                        :disabled="runInProgress || !hasUsableSources"
                        class="p-button-primary ai-prepop-run-btn"
                        @click="runPrePopulation"
                    />
                </div>

                <!-- Post-run summary — shown only when there's a
                     last-run AND we're not in the middle of a fresh run.
                     Mutually exclusive with the Ready panel above. -->
                <div v-if="lastRun && !runInProgress" class="ai-prepop-run-result">
                    <div class="ai-prepop-run-result-header">
                        <template v-if="lastRun.status === 'completed'">
                            <i class="pi pi-check-circle ai-prepop-icon-ok"></i>
                            <strong>Pre-population complete</strong>
                        </template>
                        <template v-else-if="lastRun.status === 'failed'">
                            <i class="pi pi-times-circle ai-prepop-icon-fail"></i>
                            <strong>Pre-population failed</strong>
                        </template>
                        <template v-else>
                            <i class="pi pi-info-circle"></i>
                            <strong>{{ lastRun.status }}</strong>
                        </template>
                    </div>
                    <div v-if="lastSummary" class="ai-prepop-run-result-stats">
                        <span class="ai-prepop-badge ai-prepop-badge-high">
                            {{ lastSummary.high || 0 }} high confidence
                        </span>
                        <span class="ai-prepop-badge ai-prepop-badge-medium">
                            {{ lastSummary.medium || 0 }} needs review
                        </span>
                        <span class="ai-prepop-badge ai-prepop-badge-low">
                            {{ lastSummary.low || 0 }} low confidence
                        </span>
                        <span class="ai-prepop-badge">
                            {{ lastSummary.skipped || 0 }} not found in sources
                        </span>
                    </div>
                    <div v-if="lastRun.error_message" class="ai-prepop-run-error">
                        {{ lastRun.error_message }}
                    </div>
                    <!-- Cost + token + model info is captured in
                         vendor_ingestion_run for backend audit; no need
                         to surface it to admins. -->

                    <!-- Retry is only meaningful when the previous run
                         failed. Successful runs with unchanged sources
                         are deterministic (temperature=0), so re-running
                         produces the same output — no need for a button. -->
                    <Button
                        v-if="lastRun.status === 'failed'"
                        label="Retry"
                        icon="pi pi-refresh"
                        class="p-button-text p-button-sm ai-prepop-run-again-btn"
                        :disabled="runInProgress"
                        @click="rerunPrePopulation"
                    />
                    <!-- Notify vendor: shown only if run completed, admin
                         hasn't already dispatched or explicitly skipped the
                         notification, AND this isn't a new vendor -- their
                         welcome email is mandatory and sent automatically
                         once the wizard finishes (see isNewVendor prop doc
                         above), not reviewed here. -->
                    <Button
                        v-if="!isNewVendor && lastRun.status === 'completed' && !notificationAlreadyHandled"
                        label="Notify vendor to review"
                        icon="pi pi-envelope"
                        class="p-button-text p-button-sm ai-prepop-notify-btn"
                        @click="openNotificationDialog"
                    />
                    <div
                        v-else-if="lastRun.notification_status === 'sent'"
                        class="ai-prepop-notify-status"
                    >
                        <i class="pi pi-check-circle"></i>
                        Notified {{ lastRun.notification_recipient_email }}
                    </div>
                    <div
                        v-else-if="lastRun.notification_status === 'do_not_send'"
                        class="ai-prepop-notify-status"
                    >
                        <i class="pi pi-info-circle"></i>
                        Vendor notification skipped
                    </div>
                </div>
            </template>
        </div>

        <!-- Vendor notification dialog (spec section 7). Opens
             automatically after a successful Stage 3 run; can also be
             re-opened via the "Notify vendor to review" button. -->
        <VendorNotificationDialog
            v-if="partnerId"
            v-model="notificationDialogOpen"
            :partner-id="partnerId"
            :run-id="notificationRunId"
            :vendor-name="vendorName"
            @sent="onNotificationSent"
            @skipped="onNotificationSkipped"
        />

        <!-- Footer navigation is owned by the parent (AddPartner.vue) so
             Skip + Continue sit on the same row. -->
    </div>
</template>

<script>
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import VendorSourceService from '../../service/VendorSourceService';
import VendorNotificationDialog from './VendorNotificationDialog.vue';

const MAX_URLS = 5;
const MAX_DOCS = 5;
const MAX_DOC_SIZE_BYTES = 20 * 1024 * 1024;

const SUPPORTED_DOC_MIMES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export default {
    name: 'AiPrePopulationStep',
    components: { InputText, Button, VendorNotificationDialog },
    props: {
        // Target vendor account UUID.
        partnerId: { type: String, required: true },
        // Question set id (numeric) — typically SE Question Library id.
        // Env-specific; resolve at the parent level.
        qsid: { type: [Number, String], required: true },
        // Vendor display name (for the confirmation summary in Stage 3
        // when wired up).
        vendorName: { type: String, default: '' },
        // When true, the admin-reviewed VendorNotificationDialog (Edit/
        // Send/Skip) is never shown -- a new vendor's welcome email is
        // mandatory (it carries their login credentials) and is sent
        // automatically once the wizard finishes, not reviewed here.
        isNewVendor: { type: Boolean, default: false },
    },
    emits: ['skip', 'sources-changed', 'run-state-changed'],
    data() {
        return {
            MAX_URLS,
            MAX_DOCS,
            urlRows: [{ url: '' }],
            documents: [],
            isDragOver: false,
            vendorSourceService: new VendorSourceService(),
            // Stage 2 state
            coverage: null,          // last computed estimate
            coverageLoading: false,
            coverageError: null,
            // Stage 3 state
            scope: 'fill_empty_only',
            runInProgress: false,
            lastRun: null,           // most recent run row
            // Vendor notification dialog (spec section 7) — opens
            // automatically after a completed run, points to that run's id.
            notificationDialogOpen: false,
            notificationRunId: null,
            lastSummary: null,       // {extracted, high, medium, low, skipped}
        };
    },
    computed: {
        hasUsableSources() {
            const retrievedUrls = this.urlRows.filter((r) => r.id && r.fetch_status === 'retrieved').length;
            const processedDocs = this.documents.filter((d) => d.upload_status === 'processed').length;
            return retrievedUrls > 0 || processedDocs > 0;
        },
        // A stable string identity for the current source pool — bumps
        // whenever a URL / doc is added, removed, or changes fetch/
        // upload status. Watched below so Stage 2 coverage estimate and
        // Stage 3 last-run summary can be invalidated on any change.
        sourceSignature() {
            const urlIds = this.urlRows
                .filter((r) => r.id)
                .map((r) => `u:${r.id}:${r.fetch_status}`)
                .sort()
                .join('|');
            const docIds = this.documents
                .filter((d) => d.id)
                .map((d) => `d:${d.id}:${d.upload_status}`)
                .sort()
                .join('|');
            return `${urlIds}#${docIds}`;
        },
        scopeLabel() {
            return this.scope === 'overwrite_all'
                ? 'Overwrite and refresh all answers'
                : 'Fill empty answers only';
        },
        // True once admin has dispatched OR explicitly skipped notification
        // for the current run. Drives the visibility of the "Notify vendor"
        // button vs the status line.
        notificationAlreadyHandled() {
            const s = this.lastRun?.notification_status;
            return s === 'sent' || s === 'do_not_send';
        },
    },
    mounted() {
        this.refreshSources().then(() => {
            if (this.hasUsableSources) {
                this.fetchCoverage();
            }
        });
    },
    methods: {
        async refreshSources() {
            const res = await this.vendorSourceService.listSources(this.partnerId, this.qsid);
            const data = res?.data?.data;
            if (!data) return;
            // Seed urlRows with persisted rows + keep one trailing blank
            // for adding more.
            const persistedUrls = (data.urls || []).map((u) => ({ ...u }));
            this.urlRows = persistedUrls.length < MAX_URLS
                ? [...persistedUrls, { url: '' }]
                : persistedUrls;
            this.documents = (data.documents || []).map((d) => ({ ...d }));
            this.$emit('sources-changed', { urls: persistedUrls, documents: this.documents });
        },

        addUrlRow() {
            if (this.urlRows.length >= MAX_URLS) return;
            this.urlRows.push({ url: '' });
        },

        async fetchUrl(idx) {
            const row = this.urlRows[idx];
            if (!row.url || row.fetching) return;
            row.fetching = true;
            try {
                const res = await this.vendorSourceService.fetchUrl(this.partnerId, this.qsid, row.url);
                const saved = res?.data?.data?.source;
                if (saved) {
                    this.urlRows.splice(idx, 1, { ...saved });
                    // Make sure a trailing blank row exists for more URLs.
                    if (this.urlRows.length < MAX_URLS && !this.urlRows.some((r) => !r.id && !r.url)) {
                        this.urlRows.push({ url: '' });
                    }
                    this.$emit('sources-changed');
                } else {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Could not fetch URL',
                        detail: res?.data?.message || 'Unknown error',
                        life: 4000,
                    });
                    row.fetching = false;
                }
            } catch (err) {
                row.fetching = false;
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not fetch URL',
                    detail: err?.message || 'Unknown error',
                    life: 4000,
                });
            }
        },

        // Remove a draft URL slot that was never fetched. No backend call
        // — just drops the row from the local array. Guarded at the
        // template level so we never remove the last remaining row.
        removeDraftUrl(idx) {
            const row = this.urlRows[idx];
            if (!row || row.id) return;
            this.urlRows.splice(idx, 1);
        },

        async removeUrl(idx) {
            const row = this.urlRows[idx];
            if (!row.id) return;
            row.deleting = true;
            try {
                await this.vendorSourceService.deleteUrl(this.partnerId, row.id);
                this.urlRows.splice(idx, 1);
                if (!this.urlRows.some((r) => !r.id && !r.url)) {
                    this.urlRows.push({ url: '' });
                }
                this.$emit('sources-changed');
            } catch (err) {
                row.deleting = false;
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not remove URL',
                    detail: err?.message || 'Unknown error',
                    life: 4000,
                });
            }
        },

        statusLabel(s) {
            if (s === 'retrieved') return 'Retrieved';
            if (s === 'partial') return 'Partial';
            if (s === 'failed') return 'Failed';
            return s;
        },

        urlStatusMessage(row) {
            const wc = row.word_count ? `${row.word_count.toLocaleString()} words extracted` : null;
            if (row.fetch_status === 'retrieved') {
                if (row.content_summary) return row.content_summary;
                return wc || 'content extracted';
            }
            if (row.fetch_status === 'partial') {
                const head = row.content_summary || 'page retrieved but content is limited';
                const tail = row.error_message || '';
                return tail ? `${head}. ${tail}` : head;
            }
            if (row.fetch_status === 'failed') {
                return row.error_message || 'Could not retrieve URL';
            }
            return '';
        },

        // ===== Documents =====

        openFilePicker() {
            this.$refs.fileInput?.click();
        },

        onDragOver() {
            if (this.documents.length >= MAX_DOCS) return;
            this.isDragOver = true;
        },
        onDragLeave() {
            this.isDragOver = false;
        },
        onDrop(e) {
            this.isDragOver = false;
            if (this.documents.length >= MAX_DOCS) return;
            const files = Array.from(e.dataTransfer.files || []);
            this.queueFiles(files);
        },

        onFilesChosen(e) {
            const files = Array.from(e.target.files || []);
            this.queueFiles(files);
            // Reset so picking the same file twice still triggers @change
            e.target.value = '';
        },

        async queueFiles(files) {
            for (const file of files) {
                if (this.documents.length >= MAX_DOCS) {
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Document limit reached',
                        detail: `Maximum ${MAX_DOCS} documents per vendor.`,
                        life: 3000,
                    });
                    return;
                }
                if (!SUPPORTED_DOC_MIMES.includes(file.type)) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Unsupported file type',
                        detail: 'Upload PDF, DOCX, or PPTX.',
                        life: 4000,
                    });
                    continue;
                }
                if (file.size > MAX_DOC_SIZE_BYTES) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'File too large',
                        detail: `${file.name} exceeds the 20 MB limit.`,
                        life: 4000,
                    });
                    continue;
                }
                // eslint-disable-next-line no-await-in-loop
                await this.uploadOne(file);
            }
        },

        async uploadOne(file) {
            const tempKey = `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const placeholder = {
                tempKey,
                file_name: file.name,
                file_size_bytes: file.size,
                uploading: true,
                progress: 0,
            };
            this.documents.push(placeholder);
            try {
                const res = await this.vendorSourceService.uploadDocument(
                    this.partnerId,
                    this.qsid,
                    file,
                    {
                        onProgress: (pct) => {
                            const found = this.documents.find((d) => d.tempKey === tempKey);
                            if (found) found.progress = pct;
                        },
                    }
                );
                const saved = res?.data?.data?.source;
                const idx = this.documents.findIndex((d) => d.tempKey === tempKey);
                if (saved && idx >= 0) {
                    this.documents.splice(idx, 1, { ...saved });
                } else {
                    this.documents.splice(idx, 1);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Upload failed',
                        detail: res?.data?.message || 'Unknown error',
                        life: 4000,
                    });
                }
                this.$emit('sources-changed');
            } catch (err) {
                const idx = this.documents.findIndex((d) => d.tempKey === tempKey);
                if (idx >= 0) this.documents.splice(idx, 1);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Upload failed',
                    detail: err?.message || 'Unknown error',
                    life: 4000,
                });
            }
        },

        async removeDocument(doc) {
            if (!doc.id) {
                // Only a placeholder (mid-upload) — just drop it
                this.documents = this.documents.filter((d) => d !== doc);
                return;
            }
            doc.deleting = true;
            try {
                await this.vendorSourceService.deleteDocument(this.partnerId, doc.id);
                this.documents = this.documents.filter((d) => d.id !== doc.id);
                this.$emit('sources-changed');
            } catch (err) {
                doc.deleting = false;
                this.$toast.add({
                    severity: 'error',
                    summary: 'Could not remove document',
                    detail: err?.message || 'Unknown error',
                    life: 4000,
                });
            }
        },

        formatBytes(bytes) {
            if (!bytes && bytes !== 0) return '';
            if (bytes < 1024) return `${bytes} B`;
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
            return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
        },

        formatNumber(n) {
            if (n == null || Number.isNaN(n)) return '0';
            return Number(n).toLocaleString();
        },

        // Kick a Stage 2 coverage estimate.
        async fetchCoverage() {
            if (!this.hasUsableSources) return;
            this.coverageLoading = true;
            this.coverageError = null;
            try {
                const res = await this.vendorSourceService.getCoverageEstimate(
                    this.partnerId,
                    this.qsid
                );
                const est = res?.data?.data?.estimate;
                if (est && !est.skipped_reason) {
                    this.coverage = est;
                } else if (est?.skipped_reason === 'no-usable-sources') {
                    this.coverage = est;
                } else {
                    this.coverageError = res?.data?.message || 'estimate failed';
                }
            } catch (err) {
                this.coverageError = err?.message || 'estimate failed';
            } finally {
                this.coverageLoading = false;
            }
        },

        // Fetch the most recent run for display on load.
        async fetchLastRun() {
            try {
                const res = await this.vendorSourceService.listRuns(
                    this.partnerId,
                    this.qsid
                );
                const runs = res?.data?.data?.runs || [];
                if (runs.length > 0) {
                    this.lastRun = runs[0];
                }
            } catch (_) { /* non-fatal */ }
        },

        // "Run again" click handler — clears the previous summary panel
        // and immediately fires a new run using the currently-selected
        // scope. Behaviour matches the button label instead of just
        // dismissing the summary.
        async rerunPrePopulation() {
            this.lastRun = null;
            this.lastSummary = null;
            await this.runPrePopulation();
        },

        // Manually re-open the notification dialog for the current run
        // — used when the admin dismissed it and wants to send later.
        openNotificationDialog() {
            if (!this.lastRun?.id) return;
            this.notificationRunId = this.lastRun.id;
            this.notificationDialogOpen = true;
        },

        // Dialog callback — email was sent successfully. Merge the
        // returned run row into local state so the "Notified X" status
        // line renders without a full refetch.
        onNotificationSent(payload) {
            if (payload?.run && this.lastRun?.id === payload.run.id) {
                this.lastRun = payload.run;
            }
        },

        // Dialog callback — admin explicitly chose Do-not-send.
        onNotificationSkipped(payload) {
            if (payload?.run && this.lastRun?.id === payload.run.id) {
                this.lastRun = payload.run;
            }
        },

        // Kick a Stage 3 AI pre-population run.
        async runPrePopulation() {
            if (this.runInProgress || !this.hasUsableSources) return;
            this.runInProgress = true;
            this.lastSummary = null;
            try {
                const res = await this.vendorSourceService.runIngestion(
                    this.partnerId,
                    this.qsid,
                    this.scope
                );
                const run = res?.data?.data?.run;
                const summary = res?.data?.data?.summary;
                if (run) {
                    this.lastRun = run;
                    this.lastSummary = summary;
                    this.$emit('sources-changed');
                    if (run.status === 'completed') {
                        this.$toast.add({
                            severity: 'success',
                            summary: 'AI Pre-Population complete',
                            detail: summary
                                ? `${summary.high || 0} high · ${summary.medium || 0} review · ${summary.low || 0} low`
                                : 'Answer Bank updated.',
                            life: 5000,
                        });
                        // Auto-open the vendor notification dialog on
                        // successful runs — admin picks Edit/Send/Skip
                        // next. Do NOT auto-open on failed runs, and never
                        // for a new vendor -- their welcome email (with
                        // credentials) is mandatory and sent automatically
                        // by the parent wizard, not reviewed here.
                        if (!this.isNewVendor) {
                            this.notificationRunId = run.id;
                            this.notificationDialogOpen = true;
                        }
                    } else if (run.status === 'failed') {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'AI Pre-Population failed',
                            detail: run.error_message || 'Unknown error',
                            life: 6000,
                        });
                    }
                } else {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'AI Pre-Population failed',
                        detail: res?.data?.message || 'Unknown error',
                        life: 5000,
                    });
                }
            } catch (err) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'AI Pre-Population failed',
                    detail: err?.message || 'Unknown error',
                    life: 5000,
                });
            } finally {
                this.runInProgress = false;
            }
        },
    },
    watch: {
        // Any Stage-1 change (add/remove URL, add/remove doc, status
        // change) invalidates both the Stage-2 coverage estimate and
        // the Stage-3 last-run summary — both were computed against
        // the previous source pool.
        //
        // Coverage is re-fetched automatically when the pool still has
        // usable sources (calling Claude again is cheap; Haiku 4.5).
        // The last-run summary is cleared, so the Ready-to-run panel
        // shows again with the updated source counts.
        sourceSignature(next, prev) {
            if (next === prev) return;
            // Clear stale last-run summary — the run reflects the
            // old source pool; admin should re-run with the new one.
            this.lastRun = null;
            this.lastSummary = null;
            // Coverage refresh: skip on the initial null → populated
            // transition (mounted() already kicks that fetch); only
            // re-fetch on genuine pool changes.
            if (prev === undefined || prev === '') return;
            if (!this.hasUsableSources) {
                this.coverage = null;
                return;
            }
            this.fetchCoverage();
        },

        // The parent (AddPartner.vue) can't reactively track runInProgress
        // / lastRun through $refs -- a computed reading them via $refs only
        // registers a dependency if the ref was already populated the FIRST
        // time that computed evaluated, which isn't guaranteed (this
        // component may not be mounted yet on that first pass). Emit
        // instead so the parent holds its own reactive copy.
        runInProgress: {
            handler() {
                this.$emit('run-state-changed', { runInProgress: this.runInProgress, lastRun: this.lastRun });
            },
        },
        lastRun: {
            handler() {
                this.$emit('run-state-changed', { runInProgress: this.runInProgress, lastRun: this.lastRun });
            },
        },
    },
};
</script>

<style scoped>
.ai-prepop-step {
    /* Horizontal padding matches AddPartner.vue's .step-title (0 1rem)
       so this component's content aligns with the h4 title rendered
       above it. */
    padding: 1rem 1rem;
}

.ai-prepop-subtitle {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
}
.ai-prepop-subtitle-hint {
    color: #6c757d;
    font-size: 0.85rem;
}
.ai-prepop-optional-pill {
    background: #e0f3f1;
    color: #008a83;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 0.75rem;
    font-weight: 600;
    flex-shrink: 0;
}

.ai-prepop-intro {
    background: #f1faf3;
    border: 1px solid #cce8d4;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    color: #2e5a39;
    margin-bottom: 1.5rem;
    line-height: 1.5;
}
.ai-prepop-intro-icon {
    font-size: 1.2rem;
    color: #008a83;
    flex-shrink: 0;
}

.ai-prepop-stage {
    border: 1px solid #e3e6eb;
    border-radius: 8px;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.25rem;
    background: #fff;
}
.ai-prepop-stage.disabled {
    opacity: 0.55;
}
.ai-prepop-stage-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
}
.ai-prepop-stage-num {
    background: #34a853;
    color: #fff;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.9rem;
}
.ai-prepop-stage-title {
    font-weight: 600;
    font-size: 1.05rem;
}
.ai-prepop-stage-placeholder {
    color: #6c757d;
    font-style: italic;
}

/* Every direct child of a stage (except the header row itself)
   aligns to the stage-header title text — indented past the 28px
   green number circle + 0.75rem gap. Keeps sub-content visually
   under "Gather sources" / "Review source summary" / etc., not
   flush with the circle. */
.ai-prepop-stage > *:not(.ai-prepop-stage-header) {
    margin-left: calc(28px + 0.75rem);
}
.ai-prepop-substage {
    margin-bottom: 1.25rem;
}
.ai-prepop-label-row {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}
.ai-prepop-label {
    font-weight: 600;
    color: #333;
}
.ai-prepop-label-hint {
    color: #6c757d;
    font-size: 0.85rem;
}

.ai-prepop-url-row {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    align-items: center;
}
.ai-prepop-url-input {
    flex: 1;
    margin-left: 0 !important;
}
.ai-prepop-fetch-btn {
    min-width: 90px;
}

.ai-prepop-status-pill {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    margin: 0.25rem 0 0.75rem 0;
    font-size: 0.9rem;
    line-height: 1.4;
}
.ai-prepop-status-pill.status-retrieved {
    background: #e8f5ea;
    color: #1e6b2f;
}
.ai-prepop-status-pill.status-partial {
    background: #fff8e1;
    color: #8a6500;
}
.ai-prepop-status-pill.status-failed {
    background: #fdecea;
    color: #8a1c14;
}
.ai-prepop-status-icon {
    flex-shrink: 0;
}

.ai-prepop-add-url {
    margin-top: 0.5rem;
    /* AddPartner.vue globally forces .btn { width: 8rem } via a :deep()
       selector — override that here so the label doesn't wrap. */
    width: auto !important;
    min-width: 11rem;
    white-space: nowrap;
    /* PrimeVue's default outlined-button styling for the theme in this
       repo is very muted — reads as disabled/faded. Give it explicit
       teal border + text so it looks like an actionable button. */
    color: #008a83 !important;
    border-color: #008a83 !important;
    background: transparent !important;
}
.ai-prepop-add-url:hover:not(:disabled) {
    background: #f0faf8 !important;
}

/* Icon-only remove buttons — force a proper circle with the X icon
   dead center. Every dimension is locked so no inherited width
   (AddPartner's global .btn width: 8rem, PrimeVue's .p-button min-width,
   etc.) can stretch the button into an ellipse. */
.ai-prepop-icon-btn {
    width: 2rem !important;
    height: 2rem !important;
    min-width: 2rem !important;
    max-width: 2rem !important;
    min-height: 2rem !important;
    max-height: 2rem !important;
    padding: 0 !important;
    aspect-ratio: 1 / 1;
    box-sizing: border-box !important;
    line-height: 1 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0;
    border-radius: 50% !important;
}
.ai-prepop-icon-btn :deep(.p-button-icon) {
    margin: 0 !important;
    font-size: 0.85rem;
    line-height: 1;
}

.ai-prepop-dropzone {
    border: 2px dashed #c5cad1;
    border-radius: 8px;
    padding: 2rem;
    text-align: center;
    background: #fafbfc;
    transition: background 0.15s, border-color 0.15s;
}
.ai-prepop-dropzone.dragover {
    border-color: #008a83;
    background: #ecf7f6;
}
.ai-prepop-dropzone.disabled {
    opacity: 0.5;
    pointer-events: none;
}
.ai-prepop-dropzone-text {
    color: #6c757d;
    margin-bottom: 0.75rem;
}

/* Same teal-override as .ai-prepop-add-url — PrimeVue's default
   outlined-button in this theme reads as disabled/greyed out. */
.ai-prepop-browse-btn {
    color: #008a83 !important;
    border-color: #008a83 !important;
    background: #fff !important;
    width: auto !important;
    min-width: 9rem;
    white-space: nowrap;
}
.ai-prepop-browse-btn:hover:not(:disabled) {
    background: #f0faf8 !important;
}

.ai-prepop-doc-counter {
    color: #6c757d;
    font-size: 0.85rem;
    margin: 0.75rem 0;
}

.ai-prepop-doc-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e3e6eb;
    border-radius: 6px;
    margin-bottom: 0.5rem;
}
.ai-prepop-doc-info {
    flex: 1;
    display: flex;
    gap: 0.5rem;
    align-items: baseline;
}
.ai-prepop-doc-name {
    font-weight: 500;
}
.ai-prepop-doc-size {
    color: #6c757d;
    font-size: 0.85rem;
}
.ai-prepop-doc-progress {
    color: #6c757d;
    font-size: 0.85rem;
}
.ai-prepop-doc-words {
    color: #1e6b2f;
    font-size: 0.85rem;
}
.ai-prepop-doc-failed {
    color: #8a1c14;
    font-size: 0.85rem;
}

/* .ai-prepop-footer removed — parent AddPartner.vue owns the footer
   nav so Skip + Continue sit on one row. */

/* ==================== Stage 2 — coverage cards ==================== */
.ai-prepop-refresh-btn {
    margin-left: auto;
    /* Override AddPartner.vue's global .btn { width: 8rem !important }
       so "Refresh estimate" + icon fits on one line. */
    width: auto !important;
    min-width: 8rem;
    white-space: nowrap;
}
.ai-prepop-stage-error {
    color: #8a1c14;
    padding: 0.5rem 0;
}
.ai-prepop-coverage-block {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.ai-prepop-stat-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 0.75rem;
}
.ai-prepop-stat-card {
    background: #f8f9fa;
    border: 1px solid #e3e6eb;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    text-align: center;
}
.ai-prepop-stat-card-highlight {
    background: #e0f3f1;
    border-color: #a8dfd8;
}
.ai-prepop-stat-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: #008a83;
    line-height: 1.1;
}
.ai-prepop-stat-card-highlight .ai-prepop-stat-value {
    color: #005f5a;
}
.ai-prepop-stat-label {
    font-size: 0.8rem;
    color: #6c757d;
    margin-top: 0.35rem;
    line-height: 1.3;
}
.ai-prepop-section-breakdown {
    background: #fafbfc;
    border: 1px solid #e3e6eb;
    border-radius: 6px;
    padding: 0.5rem 0.75rem;
}
.ai-prepop-section-breakdown summary {
    cursor: pointer;
    font-weight: 500;
    padding: 0.25rem 0;
    outline: none;
}
.ai-prepop-section-list {
    margin-top: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}
.ai-prepop-section-row {
    display: grid;
    grid-template-columns: 2fr 1fr 3fr auto;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.85rem;
    padding: 0.25rem 0;
}
.ai-prepop-section-name {
    font-weight: 500;
}
.ai-prepop-section-count {
    color: #6c757d;
    font-size: 0.8rem;
}
.ai-prepop-section-bar {
    background: #e9ecef;
    height: 6px;
    border-radius: 3px;
    overflow: hidden;
}
.ai-prepop-section-bar-fill {
    background: #008a83;
    height: 100%;
    transition: width 0.3s ease;
}
.ai-prepop-section-pct {
    font-weight: 500;
    color: #008a83;
    min-width: 3rem;
    text-align: right;
}

/* ==================== Stage 3 — scope + run ==================== */
.ai-prepop-scope-label {
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
}
.ai-prepop-scope-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 1rem;
}
@media (max-width: 700px) {
    .ai-prepop-scope-options { grid-template-columns: 1fr; }
}
.ai-prepop-scope-card {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    padding: 1rem;
    border: 1px solid #d5d9df;
    border-radius: 8px;
    cursor: pointer;
    background: #fff;
    transition: border-color 0.15s, background 0.15s;
}
.ai-prepop-scope-card:hover {
    border-color: #008a83;
}
.ai-prepop-scope-card.selected {
    border-color: #008a83;
    background: #f0faf8;
}
.ai-prepop-scope-card input[type="radio"] {
    margin-top: 0.25rem;
    flex-shrink: 0;
}
.ai-prepop-scope-title {
    font-weight: 600;
    color: #333;
    margin-bottom: 0.25rem;
}
.ai-prepop-scope-desc {
    color: #6c757d;
    font-size: 0.85rem;
    line-height: 1.4;
}
.ai-prepop-run-summary {
    display: flex;
    gap: 1rem;
    align-items: center;
    padding: 1rem 1.25rem;
    background: #f1faf3;
    border: 1px solid #cce8d4;
    border-radius: 8px;
    color: #2e5a39;
    line-height: 1.5;
}
.ai-prepop-run-summary > div {
    flex: 1;
}
.ai-prepop-run-btn {
    background: #008a83 !important;
    border-color: #008a83 !important;
    flex-shrink: 0;
    /* Override AddPartner.vue's global .btn { width: 8rem !important }
       so "✦ Run AI Pre-Population" fits on one line. */
    width: auto !important;
    min-width: 13rem;
    white-space: nowrap;
}
.ai-prepop-run-result {
    padding: 1rem 1.25rem;
    border: 1px solid #e3e6eb;
    border-radius: 8px;
    background: #fafbfc;
}
.ai-prepop-run-result-header {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin-bottom: 0.75rem;
    font-size: 1rem;
}
.ai-prepop-icon-ok { color: #008a83; }
.ai-prepop-icon-fail { color: #8a1c14; }
.ai-prepop-run-result-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}
.ai-prepop-badge {
    padding: 0.25rem 0.65rem;
    border-radius: 12px;
    font-size: 0.8rem;
    background: #e9ecef;
    color: #495057;
}
.ai-prepop-badge-high { background: #d4edda; color: #155724; }
.ai-prepop-badge-medium { background: #fff3cd; color: #856404; }
.ai-prepop-badge-low { background: #f8d7da; color: #721c24; }
.ai-prepop-run-error {
    color: #8a1c14;
    padding: 0.5rem 0;
    font-size: 0.9rem;
}
/* Notify-vendor + notification-status lines shown inside the completion
   panel after a successful Stage 3 run. */
.ai-prepop-notify-btn {
    color: #008a83 !important;
    background: transparent !important;
    width: auto !important;
    min-width: 12rem;
    white-space: nowrap;
    margin-top: 0.5rem;
    margin-left: 0.5rem;
}
.ai-prepop-notify-btn:hover:not(:disabled) {
    background: #f0faf8 !important;
    text-decoration: underline;
}
.ai-prepop-notify-status {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.75rem;
    background: #f0faf8;
    border: 1px solid #cce8d4;
    color: #2e5a39;
    border-radius: 6px;
    font-size: 0.85rem;
    margin-top: 0.5rem;
}

/* Run-again text button — PrimeVue's default p-button-text renders very
   muted in this theme (reads as disabled/blurry). Force explicit teal
   color, sensible width, and single-line behaviour. */
.ai-prepop-run-again-btn {
    color: #008a83 !important;
    background: transparent !important;
    width: auto !important;
    min-width: 7rem;
    white-space: nowrap;
    margin-top: 0.5rem;
}
.ai-prepop-run-again-btn:hover:not(:disabled) {
    background: #f0faf8 !important;
    text-decoration: underline;
}
</style>
